import {
  forwardRef, useRef, useMemo, useEffect, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import classNames from 'classnames/bind';

import { checkAndRoun } from '../../../../../../../helpers/topicsHelpers';
import { UK_INSIGHTS, UK_INSIGHTS_TRIAL } from '../../../../../../../data/features';
import { UKDisableMessage } from '../../../../../../../data/ukPrivateCompanyInsights';
import FilingsSvc from '../../../../../../../services/dbServices/FilingsSvc';

import Preloader from '../../../../../../Preloader';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const ChartModal = forwardRef(({
  onClose,
  style,
  x,
  y,
  date,
  currentValue,
  timeFilter,
  extraValues,
  // filingId,
}, ref) => {
  const cfMainWrapper = useRef(document.querySelector('.cf-main-wrapper'));
  const styles = useMemo(() => ({
    top: `${y}px`,
    left: `${x}px`,
    ...style,
  }), [x, y, style]);
  const hasInsightsAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS)
  ));
  const hasInsightsTrialAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS_TRIAL)
  )) && !hasInsightsAccess;
  const [currentFiling, setCurrentFiling] = useState({
    isLoading: false,
    currentData: null,
  });
  const {
    sharePrice, marketCapitalization, filingID, adjustedSharePrice,
  } = extraValues || {};

  const headerDate = () => {
    if (timeFilter === '1y') {
      return moment(date).format('MMM YYYY');
    }
    return moment(date).format('DD MMM YYYY');
  };

  useEffect(() => {
    let mounted = true;

    if (!filingID) return;

    if (!hasInsightsAccess) {
      setCurrentFiling({
        isLoading: false,
        currentData: null,
      });
      return;
    }

    setCurrentFiling({
      isLoading: true,
      currentData: null,
    });
    const callAPI = async () => {
      const filing = await FilingsSvc.getFilling({
        param: { filing_id: filingID },
      });

      const sharesAllotted = filing?.extractedData?.sharesAllotted || [];
      const foundOrdinary = sharesAllotted.find((f) => f.classOfShares.toLowerCase().includes('ordinary'));
      const foundPreferred = sharesAllotted.find((f) => f.classOfShares.toLowerCase().includes('preferred'));

      if (!mounted) return;
      if (filing) {
        setCurrentFiling({
          isLoading: false,
          currentData: {
            foundOrdinary,
            foundPreferred,
          },
        });
      } else {
        setCurrentFiling({
          isLoading: false,
          currentData: null,
        });
      }
    };

    // TODO: will be done once we get data from the API
    // callAPI();
    setCurrentFiling({
      isLoading: false,
      currentData: null,
    });

    return () => {
      mounted = false;
    };
  }, [filingID]);

  const takeSharePrice = adjustedSharePrice || sharePrice;
  const disableMessage = UKDisableMessage(hasInsightsTrialAccess);

  return (
    createPortal(
      <div
        className={cx('chart-modal', { no_access: !hasInsightsAccess })}
        ref={ref}
        style={styles}
      >
        <div className={Styles['chart-modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </div>
        <div className={Styles['chart-modal-header']}>
          {headerDate()}
        </div>
        <div className={Styles['chart-modal-lists']}>
          <div className={Styles['chart-modal-list']}>
            <div className={Styles['chart-modal-list-htitle']}>Price Per Share</div>
          </div>
          <div className={Styles['chart-modal-list']}>
            <div className={Styles['chart-modal-list-title']}>Ordinary</div>
            <div className={Styles['chart-modal-list-value']}>
              {takeSharePrice || takeSharePrice === 0 ? checkAndRoun(takeSharePrice, 2, true, true, true) : '---'}
            </div>
          </div>
          {/* TODO: Uncomment in future */}
          {/* <div className={Styles['chart-modal-list']}>
            <div className={Styles['chart-modal-list-title']}>Preferred</div>
            <div className={Styles['chart-modal-list-value']}>1.80</div>
          </div> */}
        </div>
        {currentFiling.isLoading ? (
          <div>
            <Preloader
              className={Styles['chart-modal-loader']}
              loading
              static
              top="unset"
            />
          </div>
        ) : (
          <>
            {(currentFiling.currentData?.foundOrdinary
            || currentFiling.currentData?.foundPreferred) && (
              <div className={Styles['chart-modal-lists']}>
                <div className={Styles['chart-modal-list']}>
                  <div className={Styles['chart-modal-list-htitle']}>Shares Allotted</div>
                </div>
                {currentFiling.currentData?.foundOrdinary && (
                  <div className={Styles['chart-modal-list']}>
                    <div className={Styles['chart-modal-list-title']}>Ordinary</div>
                    <div className={Styles['chart-modal-list-value']}>
                      {checkAndRoun(currentFiling.currentData?.foundOrdinary.numberAllotted, 2, true)}
                    </div>
                  </div>
                )}
                {currentFiling.currentData?.foundPreferred && (
                  <div className={Styles['chart-modal-list']}>
                    <div className={Styles['chart-modal-list-title']}>Preferred</div>
                    <div className={Styles['chart-modal-list-value']}>
                      {checkAndRoun(currentFiling.currentData?.foundPreferred.numberAllotted, 2, true)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <div className={Styles['chart-modal-lists']}>
          <div className={Styles['chart-modal-list']}>
            <div className={Styles['chart-modal-list-htitle']}>Last Round Valuation</div>
          </div>
          <div className={Styles['chart-modal-list']}>
            <div className={Styles['chart-modal-list-title']}>Ordinary</div>
            <div className={Styles['chart-modal-list-value']}>
              {marketCapitalization || marketCapitalization === 0 ? checkAndRoun(marketCapitalization, 2, true) : '---'}
            </div>
          </div>
        </div>
        {!hasInsightsAccess && (
          <div className={Styles['chart-modal-upgrade']}>
            {disableMessage}
          </div>
        )}
      </div>,
      cfMainWrapper.current,
    )
  );
});

export default ChartModal;
